import React from "react";
import { Box } from "@mui/material";
import {
  facebookEmbedUrl,
  isFacebookVideoPermalink,
} from "../../utils/facebookEmbedUrl";

type FacebookEmbedProps = {
  url: string | null | undefined;
  title?: string | null;
  /** When true (or mediaType is video), use plugins/video.php. */
  preferVideo?: boolean;
  mediaType?: string | null;
};

/**
 * Facebook plugin iframe that fills a sized Box wrapper.
 * Parent flex layouts must not collapse this — use stretch + aspect-ratio box,
 * and a native iframe (not MUI Box-as-iframe) so absolute fill works.
 */
export const FacebookEmbed: React.FC<FacebookEmbedProps> = ({
  url,
  title,
  preferVideo,
  mediaType,
}) => {
  const forceVideo =
    preferVideo === true ||
    mediaType === "video" ||
    mediaType === "reel" ||
    mediaType === "reels";

  const embedUrl = facebookEmbedUrl(url, { preferVideo: forceVideo });
  if (!embedUrl) return null;

  const isVideo = forceVideo || isFacebookVideoPermalink(url);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: isVideo ? 280 : "100%",
        mx: "auto",
        alignSelf: "stretch",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          ...(isVideo
            ? { aspectRatio: "9 / 16" }
            : { minHeight: 420 }),
          overflow: "hidden",
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      >
        <iframe
          key={embedUrl}
          src={embedUrl}
          title={title || "Facebook"}
          frameBorder={0}
          allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          scrolling="no"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
            overflow: "hidden",
          }}
        />
      </Box>
    </Box>
  );
};
